import React, { useState, useEffect } from 'react';
import Styles from './VoiceDNA.module.scss';
import { FiRefreshCw, FiInfo, FiZap, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import { FaTwitter, FaLinkedin, FaInstagram, FaYoutube, FaTiktok } from 'react-icons/fa';
import contentTrainingService from 'services/contentTrainingService';
import { success as successToast, error as errorToast } from 'helpers/toaster';

const PLATFORM_CONFIG = {
  twitter: { icon: FaTwitter, label: 'Twitter', color: '#1DA1F2' },
  linkedin: { icon: FaLinkedin, label: 'LinkedIn', color: '#0A66C2' },
  instagram: { icon: FaInstagram, label: 'Instagram', color: '#E4405F' },
  youtube: { icon: FaYoutube, label: 'YouTube', color: '#FF0000' },
  tiktok: { icon: FaTiktok, label: 'TikTok', color: '#000000' },
  website: { icon: () => <span>📝</span>, label: 'Blog', color: '#10B981' },
};

const VOICE_SPECTRUM = [
  { id: 'formal_casual', leftLabel: 'Formal', rightLabel: 'Casual' },
  { id: 'technical_simple', leftLabel: 'Technical', rightLabel: 'Simple' },
  { id: 'reserved_expressive', leftLabel: 'Reserved', rightLabel: 'Expressive' },
  { id: 'corporate_conversational', leftLabel: 'Corporate', rightLabel: 'Conversational' },
  { id: 'serious_playful', leftLabel: 'Serious', rightLabel: 'Playful' },
];

const VoiceDNA = ({ brandId, voiceDNA: initialVoiceDNA, samples, onRefresh }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [voiceDNA, setVoiceDNA] = useState(initialVoiceDNA);
  const [activeTab, setActiveTab] = useState('overview');

  const hasEnoughSamples = samples.length >= 5;

  // Analyze voice DNA
  const analyzeVoice = async () => {
    if (!brandId) return;

    setIsAnalyzing(true);
    try {
      const response = await contentTrainingService.analyzeVoiceDNA(brandId);
      if (response.data.success) {
        setVoiceDNA(response.data.data);
        successToast('Voice analysis complete');
      }
    } catch (error) {
      console.error('Voice analysis error:', error);
      errorToast('Failed to analyze voice. Make sure you have enough content samples.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Get platform samples
  const getPlatformSamples = (platform) => {
    return samples.filter(s => s.platform === platform);
  };

  // Calculate platform stats
  const getPlatformStats = (platform) => {
    const platformSamples = getPlatformSamples(platform);
    if (platformSamples.length === 0) return null;

    const totalWords = platformSamples.reduce((acc, s) => acc + (s.word_count || 0), 0);
    const avgWords = Math.round(totalWords / platformSamples.length);

    // Count emojis (simple regex)
    const emojiCount = platformSamples.reduce((acc, s) => {
      const matches = (s.content || '').match(/[\u{1F300}-\u{1F9FF}]/gu);
      return acc + (matches ? matches.length : 0);
    }, 0);
    const emojiRate = Math.round((emojiCount / platformSamples.length) * 100) / 100;

    // Count questions
    const questionCount = platformSamples.reduce((acc, s) => {
      const matches = (s.content || '').match(/\?/g);
      return acc + (matches ? matches.length : 0);
    }, 0);
    const questionRate = Math.round((questionCount / platformSamples.length / 2) * 100);

    return {
      count: platformSamples.length,
      avgWords,
      emojiRate,
      questionRate,
    };
  };

  // Get active platforms
  const activePlatforms = Object.keys(PLATFORM_CONFIG).filter(p => getPlatformSamples(p).length > 0);

  // Render spectrum slider
  const renderSpectrumSlider = (spectrum, value = 50) => {
    return (
      <div className={Styles.spectrumRow}>
        <span className={Styles.spectrumLabel}>{spectrum.leftLabel}</span>
        <div className={Styles.spectrumTrack}>
          <div
            className={Styles.spectrumFill}
            style={{ width: `${value}%` }}
          />
          <div
            className={Styles.spectrumThumb}
            style={{ left: `${value}%` }}
          />
        </div>
        <span className={Styles.spectrumLabel}>{spectrum.rightLabel}</span>
      </div>
    );
  };

  // Render platform voice card
  const renderPlatformCard = (platform) => {
    const config = PLATFORM_CONFIG[platform];
    const stats = getPlatformStats(platform);
    const Icon = config.icon;
    const platformVoice = voiceDNA?.platform_voices?.[platform];

    if (!stats) return null;

    return (
      <div key={platform} className={Styles.platformCard}>
        <div className={Styles.platformHeader}>
          <div className={Styles.platformIcon} style={{ color: config.color }}>
            <Icon />
          </div>
          <div className={Styles.platformInfo}>
            <h4>{config.label}</h4>
            <span>{stats.count} samples</span>
          </div>
        </div>

        {platformVoice ? (
          <div className={Styles.platformVoice}>
            <p className={Styles.voiceDescription}>{platformVoice.description}</p>
            <div className={Styles.voiceTags}>
              {platformVoice.characteristics?.slice(0, 3).map((char, idx) => (
                <span key={idx} className={Styles.voiceTag}>{char}</span>
              ))}
            </div>
          </div>
        ) : (
          <div className={Styles.platformStats}>
            <div className={Styles.statRow}>
              <span>Avg words</span>
              <span className={Styles.statValue}>{stats.avgWords}</span>
            </div>
            <div className={Styles.statRow}>
              <span>Emoji usage</span>
              <span className={Styles.statValue}>{stats.emojiRate}/post</span>
            </div>
            <div className={Styles.statRow}>
              <span>Questions</span>
              <span className={Styles.statValue}>{stats.questionRate}%</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={Styles.container}>
      {/* Header */}
      <div className={Styles.header}>
        <div className={Styles.headerText}>
          <h3>Voice DNA Analysis</h3>
          <p>Understand your brand's unique voice patterns across platforms</p>
        </div>
        <button
          className={Styles.analyzeBtn}
          onClick={analyzeVoice}
          disabled={isAnalyzing || !hasEnoughSamples}
        >
          {isAnalyzing ? (
            <>
              <FiRefreshCw className={Styles.spinning} />
              Analyzing...
            </>
          ) : (
            <>
              <FiZap />
              {voiceDNA ? 'Re-analyze' : 'Analyze Voice'}
            </>
          )}
        </button>
      </div>

      {/* Minimum samples warning */}
      {!hasEnoughSamples && (
        <div className={Styles.warningBanner}>
          <FiAlertCircle />
          <span>Add at least 5 content samples to enable voice analysis. You have {samples.length}.</span>
        </div>
      )}

      {/* Tab navigation */}
      <div className={Styles.tabNav}>
        <button
          className={`${Styles.tab} ${activeTab === 'overview' ? Styles.active : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`${Styles.tab} ${activeTab === 'platforms' ? Styles.active : ''}`}
          onClick={() => setActiveTab('platforms')}
        >
          By Platform
        </button>
        <button
          className={`${Styles.tab} ${activeTab === 'patterns' ? Styles.active : ''}`}
          onClick={() => setActiveTab('patterns')}
        >
          Patterns
        </button>
      </div>

      {/* Tab content */}
      <div className={Styles.tabContent}>
        {activeTab === 'overview' && (
          <div className={Styles.overviewTab}>
            {/* Voice Spectrum */}
            <div className={Styles.section}>
              <div className={Styles.sectionHeader}>
                <h4>Voice Spectrum</h4>
                <FiInfo className={Styles.infoIcon} title="Based on analysis of your content" />
              </div>

              <div className={Styles.spectrumContainer}>
                {VOICE_SPECTRUM.map(spectrum => (
                  <div key={spectrum.id}>
                    {renderSpectrumSlider(
                      spectrum,
                      voiceDNA?.spectrum?.[spectrum.id] || 50
                    )}
                  </div>
                ))}
                {!voiceDNA && (
                  <div className={Styles.spectrumOverlay}>
                    <p>Run voice analysis to see your spectrum</p>
                  </div>
                )}
              </div>
            </div>

            {/* Signature Phrases */}
            <div className={Styles.section}>
              <div className={Styles.sectionHeader}>
                <h4>Signature Phrases</h4>
                <span className={Styles.sectionSubtitle}>Phrases you use frequently</span>
              </div>

              {voiceDNA?.signature_phrases?.length > 0 ? (
                <div className={Styles.phrasesList}>
                  {voiceDNA.signature_phrases.map((phrase, idx) => (
                    <span key={idx} className={Styles.phrase}>"{phrase}"</span>
                  ))}
                </div>
              ) : (
                <div className={Styles.emptySection}>
                  <p>Signature phrases will appear after voice analysis</p>
                </div>
              )}
            </div>

            {/* Consistency Score */}
            <div className={Styles.section}>
              <div className={Styles.sectionHeader}>
                <h4>Cross-Platform Consistency</h4>
              </div>

              <div className={Styles.consistencyMeter}>
                <div className={Styles.meterTrack}>
                  <div
                    className={Styles.meterFill}
                    style={{ width: `${voiceDNA?.consistency_score || 0}%` }}
                  />
                </div>
                <div className={Styles.meterLabels}>
                  <span>0%</span>
                  <span className={Styles.meterValue}>
                    {voiceDNA?.consistency_score ? `${voiceDNA.consistency_score}%` : 'Not analyzed'}
                  </span>
                  <span>100%</span>
                </div>
              </div>

              {voiceDNA?.consistency_insights && (
                <div className={Styles.insightsList}>
                  {voiceDNA.consistency_insights.consistent?.map((item, idx) => (
                    <div key={idx} className={Styles.insightItem}>
                      <FiCheckCircle className={Styles.insightIconGreen} />
                      <span>Consistent: {item}</span>
                    </div>
                  ))}
                  {voiceDNA.consistency_insights.varies?.map((item, idx) => (
                    <div key={idx} className={Styles.insightItem}>
                      <FiAlertCircle className={Styles.insightIconOrange} />
                      <span>Varies: {item}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'platforms' && (
          <div className={Styles.platformsTab}>
            <div className={Styles.platformGrid}>
              {activePlatforms.map(platform => renderPlatformCard(platform))}
            </div>

            {activePlatforms.length === 0 && (
              <div className={Styles.emptyPlatforms}>
                <p>No platform data yet. Add content from social media to see platform-specific voice analysis.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'patterns' && (
          <div className={Styles.patternsTab}>
            {/* Vocabulary Patterns */}
            <div className={Styles.section}>
              <div className={Styles.sectionHeader}>
                <h4>Vocabulary Patterns</h4>
              </div>

              {voiceDNA?.vocabulary ? (
                <div className={Styles.vocabGrid}>
                  <div className={Styles.vocabColumn}>
                    <h5>Words You Love</h5>
                    <div className={Styles.wordCloud}>
                      {voiceDNA.vocabulary.preferred?.map((word, idx) => (
                        <span key={idx} className={Styles.preferredWord}>{word}</span>
                      ))}
                    </div>
                  </div>
                  <div className={Styles.vocabColumn}>
                    <h5>Words You Avoid</h5>
                    <div className={Styles.wordCloud}>
                      {voiceDNA.vocabulary.avoided?.map((word, idx) => (
                        <span key={idx} className={Styles.avoidedWord}>{word}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className={Styles.emptySection}>
                  <p>Run voice analysis to see vocabulary patterns</p>
                </div>
              )}
            </div>

            {/* Sentence Structure */}
            <div className={Styles.section}>
              <div className={Styles.sectionHeader}>
                <h4>Sentence Structure</h4>
              </div>

              {voiceDNA?.sentence_structure ? (
                <div className={Styles.structureStats}>
                  <div className={Styles.structureStat}>
                    <span className={Styles.structureValue}>{voiceDNA.sentence_structure.avg_length}</span>
                    <span className={Styles.structureLabel}>Avg sentence length</span>
                  </div>
                  <div className={Styles.structureStat}>
                    <span className={Styles.structureValue}>{voiceDNA.sentence_structure.paragraph_style}</span>
                    <span className={Styles.structureLabel}>Paragraph style</span>
                  </div>
                  <div className={Styles.structureStat}>
                    <span className={Styles.structureValue}>{voiceDNA.sentence_structure.list_usage}%</span>
                    <span className={Styles.structureLabel}>Uses lists</span>
                  </div>
                </div>
              ) : (
                <div className={Styles.emptySection}>
                  <p>Run voice analysis to see structure patterns</p>
                </div>
              )}
            </div>

            {/* Tone Shifts */}
            <div className={Styles.section}>
              <div className={Styles.sectionHeader}>
                <h4>Tone Patterns</h4>
              </div>

              {voiceDNA?.tone_patterns ? (
                <div className={Styles.tonePatterns}>
                  <div className={Styles.toneRow}>
                    <span className={Styles.toneLabel}>Opening</span>
                    <span className={Styles.toneValue}>{voiceDNA.tone_patterns.opening}</span>
                  </div>
                  <div className={Styles.toneRow}>
                    <span className={Styles.toneLabel}>Body</span>
                    <span className={Styles.toneValue}>{voiceDNA.tone_patterns.body}</span>
                  </div>
                  <div className={Styles.toneRow}>
                    <span className={Styles.toneLabel}>Closing</span>
                    <span className={Styles.toneValue}>{voiceDNA.tone_patterns.closing}</span>
                  </div>
                </div>
              ) : (
                <div className={Styles.emptySection}>
                  <p>Run voice analysis to see tone patterns</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceDNA;
